import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { Layout, PriceInput, Input, Checkbox } from '../../../shared/components'
import { BatteryCard } from '../components'
import { Select } from '../../../shared/components'
import { Battery } from '../../../shared/types/battery'
import { BatteryFilters } from '../../../services'
import { batteryService, brandService } from '../../../services'
import type { Brand } from '../../../services/brandService'
import { useCart } from '../../../store/modules/cart/hooks'

const CSS_HANDLES = [
  'catalog',
  'catalogHeader',
  'catalogTitle',
  'catalogSubtitle',
  'catalogContent',
  'catalogSidebar',
  'catalogFilters',
  'filterSection',
  'filterTitle',
  'filterOptions',
  'filterOption',
  'filterCheckbox',
  'filterLabel',
  'filterRange',
  'filterInput',
  'catalogMain',
  'catalogToolbar',
  'catalogResults',
  'catalogSort',
  'catalogViewToggle',
  'catalogGrid',
  'catalogPagination',
  'paginationButton',
  'paginationInfo',
  'searchBar',
  'searchInput',
  'searchButton',
  'clearFilters',
  'applyFilters',
  'backButton'
] as const

const voltages = [12, 24]

const createInitialFilters = (): BatteryFilters => ({
  search: '',
  brand: [],
  voltage: [],
  capacity_min: undefined,
  capacity_max: undefined,
  price_min: undefined,
  price_max: undefined,
  inStock: undefined,
  sortBy: 'price'
})

const sortPrimitiveArray = (values: (string | number)[]) =>
  [...values].sort((a, b) => {
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b
    }
    return String(a).localeCompare(String(b))
  })

const areArraysEqual = (valuesA?: (string | number)[], valuesB?: (string | number)[]) => {
  const a = valuesA ? sortPrimitiveArray(valuesA) : []
  const b = valuesB ? sortPrimitiveArray(valuesB) : []

  if (a.length !== b.length) {
    return false
  }

  return a.every((value, index) => value === b[index])
}

const areFiltersEqual = (a: BatteryFilters, b: BatteryFilters) => {
  return (
    a.search === b.search &&
    a.capacity_min === b.capacity_min &&
    a.capacity_max === b.capacity_max &&
    a.price_min === b.price_min &&
    a.price_max === b.price_max &&
    a.inStock === b.inStock &&
    a.sortBy === b.sortBy &&
    a.sortOrder === b.sortOrder &&
    areArraysEqual(a.brand, b.brand) &&
    areArraysEqual(a.voltage, b.voltage) &&
    areArraysEqual(a.category, b.category)
  )
}

const BatteryCatalog: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [searchParams] = useSearchParams()
  const { CATEGORIA } = useParams<{ CATEGORIA?: string }>()
  const navigate = useNavigate()
  const { openCart } = useCart()
  
  // Estado para produtos e loading
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [products, setProducts] = useState<Battery[]>([])
  
  // Estado para marcas
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandsLoading, setBrandsLoading] = useState(true)
  
  // Estado de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const itemsPerPage = 12

  // Filtros
  const [filters, setFilters] = useState<BatteryFilters>(() => createInitialFilters())
  const [pendingFilters, setPendingFilters] = useState<BatteryFilters>(() => createInitialFilters())
  
  // Flag para rastrear se já foi inicializado
  const isInitialized = useRef(false)
  const previousFiltersRef = useRef<BatteryFilters | null>(null)
  const previousPageRef = useRef<number>(1)
  const isLoadingRef = useRef(false)
  const previousCategoryRef = useRef<string | undefined>(undefined)
  const brandsLoadedRef = useRef(false)
  const isLoadingBrandsRef = useRef(false)

  // Carregar marcas da API
  useEffect(() => {
    // Evitar chamadas duplicadas (especialmente no React Strict Mode)
    if (brandsLoadedRef.current || isLoadingBrandsRef.current) {
      return
    }

    const loadBrands = async () => {
      try {
        isLoadingBrandsRef.current = true
        setBrandsLoading(true)
        const response = await brandService.list({ limit: 100 })
        setBrands(response.brands)
        brandsLoadedRef.current = true
      } catch (err) {
        console.error('Erro ao carregar marcas:', err)
        // Em caso de erro, manter array vazio
        setBrands([])
        brandsLoadedRef.current = true // Marcar como carregado mesmo em caso de erro para evitar tentativas infinitas
      } finally {
        setBrandsLoading(false)
        isLoadingBrandsRef.current = false
      }
    }

    void loadBrands()
  }, [])

  // Função para carregar produtos
  const loadProducts = useCallback(async () => {
    // Evitar chamadas simultâneas
    if (isLoadingRef.current) {
      return
    }

    try {
      isLoadingRef.current = true
      setLoading(true)
      setError(null)

      // Garantir que a categoria da URL está no filtro
      const filtersToSend: BatteryFilters = { ...filters }
      if (CATEGORIA) {
        filtersToSend.category = [CATEGORIA]
      }

      console.log('Enviando filtros para API:', { ...filtersToSend, page: currentPage, limit: itemsPerPage })

      const response = await batteryService.list({
        ...filtersToSend,
        page: currentPage,
        limit: itemsPerPage
      })

      setProducts(response.batteries)
      setTotalPages(response.pagination.totalPages)
      setTotalResults(response.pagination.total)
      
      // Atualizar referências após carregamento bem-sucedido
      previousFiltersRef.current = { ...filters }
      previousPageRef.current = currentPage
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar produtos'
      setError(message)
      console.error('Erro ao carregar produtos:', err)
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }, [filters, currentPage, itemsPerPage])

  // Aplicar filtros da URL na inicialização e quando a categoria mudar
  useEffect(() => {
    // Verificar se a categoria mudou
    const categoryChanged = previousCategoryRef.current !== CATEGORIA
    
    // Se já foi inicializado e a categoria não mudou, não fazer nada
    if (isInitialized.current && !categoryChanged) {
      return
    }

    const urlBrand = searchParams.get('brand') || searchParams.get('marca')
    const urlSearch = searchParams.get('search') || searchParams.get('q') || searchParams.get('busca')
    const urlVoltage = searchParams.get('voltage') || searchParams.get('voltagem')
    const urlCapacity = searchParams.get('capacity') || searchParams.get('amperage') || searchParams.get('capacidade')
    const urlPriceMin = searchParams.get('price_min') || searchParams.get('preco_min')
    const urlPriceMax = searchParams.get('price_max') || searchParams.get('preco_max')
    const urlKit = searchParams.get('kit')
    const urlCategory = searchParams.get('categoria') || searchParams.get('category')
    const urlVehicle = searchParams.get('veiculo') || searchParams.get('vehicle')

    const initialFilters: Partial<BatteryFilters> = {}

    // Priorizar categoria do parâmetro da URL sobre query params
    if (CATEGORIA) {
      initialFilters.category = [CATEGORIA]
    } else if (urlCategory) {
      initialFilters.category = [urlCategory]
    }

    if (urlBrand) {
      // Usar o valor da URL diretamente (pode ser nome ou slug)
      initialFilters.brand = [urlBrand]
    }

    if (urlSearch) {
      initialFilters.search = urlSearch
    }

    if (urlVoltage) {
      const voltage = parseInt(urlVoltage)
      if (!isNaN(voltage)) {
        initialFilters.voltage = [voltage]
      }
    }

    if (urlCapacity) {
      const capacity = parseFloat(urlCapacity)
      if (!isNaN(capacity)) {
        initialFilters.capacity_min = capacity
        initialFilters.capacity_max = capacity
      }
    }

    if (urlPriceMin) {
      const priceMin = parseFloat(urlPriceMin)
      if (!isNaN(priceMin)) {
        initialFilters.price_min = priceMin
      }
    }

    if (urlPriceMax) {
      const priceMax = parseFloat(urlPriceMax)
      if (!isNaN(priceMax)) {
        initialFilters.price_max = priceMax
      }
    }

    if (urlKit) {
      initialFilters.search = urlKit
    }

    if (urlVehicle) {
      initialFilters.search = urlVehicle
    }

    // Aplicar filtros iniciais se houver algum parâmetro na URL ou categoria
    if (Object.keys(initialFilters).length > 0 || CATEGORIA) {
      const newFilters = { ...createInitialFilters(), ...initialFilters }
      // Garantir que a categoria está sempre presente se CATEGORIA existir
      if (CATEGORIA) {
        newFilters.category = [CATEGORIA]
      }
      setFilters(newFilters)
      setPendingFilters(newFilters)
      setCurrentPage(1) // Resetar página quando categoria mudar
      // Marcar como inicializado após aplicar os filtros
      isInitialized.current = true
      previousCategoryRef.current = CATEGORIA
      // A busca será disparada automaticamente pelo useEffect que monitora mudanças em filters
    } else {
      // Se não houver parâmetros na URL, marcar como inicializado normalmente
      isInitialized.current = true
      previousCategoryRef.current = CATEGORIA
    }
  }, [searchParams, CATEGORIA]) // Incluir searchParams e CATEGORIA para reagir a mudanças

  // Carregar produtos da API quando filters, currentPage ou CATEGORIA mudarem
  useEffect(() => {
    // Só carregar se já foi inicializado (evita chamada na montagem antes dos filtros serem aplicados)
    if (!isInitialized.current) {
      return
    }

    // Verificar se filters ou currentPage realmente mudaram
    const filtersChanged = !previousFiltersRef.current || !areFiltersEqual(filters, previousFiltersRef.current)
    const pageChanged = previousPageRef.current !== currentPage
    const categoryChanged = previousCategoryRef.current !== CATEGORIA

    // Só carregar se realmente houver mudança OU se for a primeira vez (previousFiltersRef é null)
    if (filtersChanged || pageChanged || categoryChanged) {
      void loadProducts()
    }
  }, [filters, currentPage, CATEGORIA, loadProducts])

  const handlePendingFilterChange = (newFilters: Partial<BatteryFilters>) => {
    setPendingFilters(prev => ({ ...prev, ...newFilters }))
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handlePendingFilterChange({ search: e.target.value })
  }

  const handleBrandChange = (brandName: string) => {
    const brand = pendingFilters.brand || []
    const newBrands = brand.includes(brandName)
      ? brand.filter(b => b !== brandName)
      : [...brand, brandName]
    handlePendingFilterChange({ brand: newBrands })
  }

  const handleVoltageChange = (volt: number) => {
    const voltage = pendingFilters.voltage || []
    const newVoltages = voltage.includes(volt)
      ? voltage.filter(v => v !== volt)
      : [...voltage, volt]
    handlePendingFilterChange({ voltage: newVoltages })
  }

  const handleSortChange = (value: string) => {
    let updates: Partial<BatteryFilters> | null = null

    if (value === 'price_asc') {
      updates = { sortBy: 'price', sortOrder: 'asc' }
    } else if (value === 'price_desc') {
      updates = { sortBy: 'price', sortOrder: 'desc' }
    } else if (value === 'name') {
      updates = { sortBy: 'name', sortOrder: 'asc' }
    } else if (value === 'rating') {
      updates = { sortBy: 'rating', sortOrder: 'desc' }
    }

    if (updates) {
      setFilters(prev => ({ ...prev, ...updates }))
      setPendingFilters(prev => ({ ...prev, ...updates }))
      setCurrentPage(1)
    }
  }

  const handleApplyFilters = () => {
    if (areFiltersEqual(filters, pendingFilters)) {
      return
    }
    setFilters(() => ({ ...pendingFilters }))
    setCurrentPage(1)
  }

  const clearFilters = () => {
    const initialFilters = createInitialFilters()
    setFilters(initialFilters)
    setPendingFilters(createInitialFilters())
    setCurrentPage(1)
  }

  const hasPendingChanges = !areFiltersEqual(filters, pendingFilters)

  const handleAddToCart = (batteryId: string) => {
    // Abrir a sidebar do carrinho após adicionar o produto
    // batteryId é recebido do BatteryCard mas não é necessário aqui
    void batteryId // Marcar como usado intencionalmente
    openCart()
  }

  const renderSkeleton = () => (
    <Layout>
      <div className={`${handles.catalog} min-h-screen bg-gray-50`}>
        <div className="bg-white border-b border-gray-200 py-8">
          <div className="max-w-7xl mx-auto px-4 space-y-3">
            <div className="h-8 w-64 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
            <div className="h-4 w-48 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4" role="status" aria-live="polite" aria-busy="true">
            <div className="lg:col-span-1">
              <div className="space-y-6 rounded-lg bg-white p-6 shadow-sm">
                <div className="h-10 w-full rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                <div className="space-y-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={`filter-group-${index}`} className="space-y-3">
                      <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                      <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, optionIndex) => (
                          <div
                            key={`filter-option-${index}-${optionIndex}`}
                            className="h-4 w-40 rounded bg-gray-200 animate-pulse"
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="h-10 w-full rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                <div className="h-10 w-full rounded bg-gray-200 animate-pulse" aria-hidden="true" />
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
                <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                <div className="h-10 w-48 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={`product-card-skeleton-${index}`}
                    className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm space-y-4"
                    aria-hidden="true"
                  >
                    <div className="h-40 w-full rounded-lg bg-gray-200 animate-pulse" />
                    <div className="space-y-3">
                      <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
                      <div className="h-4 w-24 rounded bg-gray-200 animate-pulse" />
                      <div className="h-6 w-20 rounded bg-gray-200 animate-pulse" />
                    </div>
                    <div className="h-10 w-full rounded bg-gray-200 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )

  const renderEmptyState = () => (
    <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-8 py-16 text-center shadow-sm">
      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-600">
        <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 17h6m4-7h-4l-3-5-3 5H5l3.553 3.387a2 2 0 01.647 1.472V20h5.6v-5.14a2 2 0 01.647-1.473L19 10z"
          />
        </svg>
      </div>
      <h3 className="text-2xl font-semibold text-gray-900 mb-3">Nenhum resultado para a sua busca</h3>
      <p className="mx-auto mb-6 max-w-xl text-gray-600">
        Ajuste filtros, revise palavras-chave ou explore todo o catálogo para encontrar a bateria ideal para o seu veículo.
      </p>
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
        <button
          onClick={clearFilters}
          className="inline-flex items-center rounded-lg bg-blue-600 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          Limpar filtros
        </button>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center rounded-lg border border-gray-200 px-5 py-3 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          Ajustar busca
        </button>
      </div>
    </div>
  )

  if (loading) {
    return renderSkeleton()
  }

  return (
    <Layout>
      <div className={`${handles.catalog} min-h-screen bg-gray-50`}>
        {/* Header */}
        <div className={`${handles.catalogHeader} bg-white border-b border-gray-200 py-8`}>
          <div className="max-w-7xl mx-auto px-4">
            {CATEGORIA && (
              <button
                onClick={() => navigate('/catalogo')}
                className={`${handles.backButton} inline-flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors`}
                aria-label="Voltar para seleção de categorias"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Voltar para categorias
              </button>
            )}
            <h1 className={`${handles.catalogTitle} text-3xl font-bold text-gray-900 mb-2`}>
              {CATEGORIA 
                ? `Catálogo - ${CATEGORIA.charAt(0).toUpperCase() + CATEGORIA.slice(1).replace(/-/g, ' ')}`
                : 'Catálogo de Produtos'}
            </h1>
            <p className={`${handles.catalogSubtitle} text-gray-600`}>
              {`${totalResults} ${totalResults === 1 ? 'produto encontrado' : 'produtos encontrados'}`}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className={`${handles.catalogContent} max-w-7xl mx-auto px-4 py-8`}>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Filters */}
            <div className={`${handles.catalogSidebar} lg:col-span-1`}>
              <div className={`${handles.catalogFilters} bg-white rounded-lg shadow-sm p-6 sticky top-4`}>
                {/* Search Bar */}
                <div className={`${handles.searchBar} mb-6`}>
                  <Input
                    type="text"
                    placeholder="Buscar produtos..."
                    value={pendingFilters.search}
                    onChange={handleSearchChange}
                    className={`${handles.searchInput} w-full`}
                  />
                </div>

                {/* Brands Filter */}
                <div className={`${handles.filterSection} mb-6`}>
                  <h3 className={`${handles.filterTitle} text-lg font-semibold text-gray-900 mb-3`}>
                    Marcas
                  </h3>
                  {brandsLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div key={index} className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className={`${handles.filterOptions} space-y-2`}>
                      {brands.length > 0 ? (
                        brands.map(brand => (
                          <label key={brand.id} className={`${handles.filterOption} flex items-center cursor-pointer gap-2`}>
                            <Checkbox
                              checked={pendingFilters.brand?.includes(brand.name) || false}
                              onChange={() => handleBrandChange(brand.name)}
                              className={`${handles.filterCheckbox}`}
                            />
                            <span className={`${handles.filterLabel} text-sm text-gray-700 leading-5`}>
                              {brand.name}
                            </span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">Nenhuma marca disponível</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Voltage Filter */}
                <div className={`${handles.filterSection} mb-6`}>
                  <h3 className={`${handles.filterTitle} text-lg font-semibold text-gray-900 mb-3`}>
                    Voltagem
                  </h3>
                  <div className={`${handles.filterOptions} space-y-2`}>
                    {voltages.map(voltage => (
                      <label key={voltage} className={`${handles.filterOption} flex items-center cursor-pointer gap-2`}>
                        <Checkbox
                          checked={pendingFilters.voltage?.includes(voltage) || false}
                          onChange={() => handleVoltageChange(voltage)}
                          className={`${handles.filterCheckbox}`}
                        />
                        <span className={`${handles.filterLabel} text-sm text-gray-700 leading-5`}>
                          {voltage}V
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className={`${handles.filterSection} mb-6`}>
                  <h3 className={`${handles.filterTitle} text-lg font-semibold text-gray-900 mb-3`}>
                    Faixa de Preço
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <PriceInput
                        placeholder="Mínimo"
                        value={pendingFilters.price_min}
                        onChange={(value) => handlePendingFilterChange({ price_min: value })}
                        className={handles.filterInput}
                      />
                      <span className="text-gray-500">-</span>
                      <PriceInput
                        placeholder="Máximo"
                        value={pendingFilters.price_max}
                        onChange={(value) => handlePendingFilterChange({ price_max: value })}
                        className={handles.filterInput}
                      />
                    </div>
                  </div>
                </div>

                {/* Stock Filter */}
                <div className={`${handles.filterSection} mb-6`}>
                  <label className={`${handles.filterOption} flex items-center cursor-pointer`}>
                    <Checkbox
                      checked={!!pendingFilters.inStock}
                      onChange={(e) => handlePendingFilterChange({ inStock: e.target.checked })}
                      className={`${handles.filterCheckbox} mr-2`}
                    />
                    <span className={`${handles.filterLabel} text-gray-700`}>
                      Apenas em estoque
                    </span>
                  </label>
                </div>

                {/* Apply Filters */}
                <button
                  onClick={handleApplyFilters}
                  disabled={!hasPendingChanges}
                  className={`${handles.applyFilters} w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed mb-3`}
                >
                  Aplicar Filtros
                </button>

                {/* Clear Filters */}
                <button
                  onClick={clearFilters}
                  className={`${handles.clearFilters} w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors`}
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div className={`${handles.catalogMain} lg:col-span-3`}>
              {/* Toolbar */}
              <div className={`${handles.catalogToolbar} bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between`}>
                <div className={`${handles.catalogResults} text-gray-600`}>
                  {`${totalResults} ${totalResults === 1 ? 'resultado' : 'resultados'}`}
                </div>
                <div className={`${handles.catalogSort} flex items-center space-x-4`}>
                  <label className="text-gray-600">Ordenar por:</label>
                  <Select
                    value={`${filters.sortBy}_${filters.sortOrder || 'asc'}`}
                    onChange={handleSortChange}
                    options={[
                      { value: 'price_asc', label: 'Menor Preço' },
                      { value: 'price_desc', label: 'Maior Preço' },
                      { value: 'name', label: 'Nome (A-Z)' },
                      { value: 'rating', label: 'Melhor Avaliação' }
                    ]}
                    placeholder="Ordenar por"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}

              {/* Battery Grid */}
              {!error && (
                <>
                  <div className={`${handles.catalogGrid} grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8`}>
                    {products.map(product => (
                      <BatteryCard key={product.id} battery={product} onAddToCart={handleAddToCart} />
                    ))}
                  </div>

                  {products.length === 0 && renderEmptyState()}

                  {products.length > 0 && totalPages > 1 && (
                    <div className={`${handles.catalogPagination} mt-8`}>
                      {/* Informação de resultados */}
                      <div className={`${handles.paginationInfo} text-center text-sm text-gray-600 mb-4`}>
                        Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalResults)} de {totalResults} produtos
                      </div>
                      
                      {/* Controles de paginação */}
                      <nav className="flex items-center justify-center gap-2" aria-label="Paginação">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className={`${handles.paginationButton} inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors`}
                          aria-label="Página anterior"
                        >
                          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Anterior
                        </button>
                        
                        {/* Números de página */}
                        <div className="flex items-center gap-1">
                          {(() => {
                            const pages: (number | string)[] = []
                            const maxVisible = 7
                            
                            if (totalPages <= maxVisible) {
                              // Mostrar todas as páginas se houver poucas
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i)
                              }
                            } else {
                              // Lógica para mostrar páginas com ellipsis
                              if (currentPage <= 4) {
                                // Mostrar primeiras páginas
                                for (let i = 1; i <= 5; i++) {
                                  pages.push(i)
                                }
                                pages.push('ellipsis')
                                pages.push(totalPages)
                              } else if (currentPage >= totalPages - 3) {
                                // Mostrar últimas páginas
                                pages.push(1)
                                pages.push('ellipsis')
                                for (let i = totalPages - 4; i <= totalPages; i++) {
                                  pages.push(i)
                                }
                              } else {
                                // Mostrar páginas ao redor da atual
                                pages.push(1)
                                pages.push('ellipsis')
                                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                                  pages.push(i)
                                }
                                pages.push('ellipsis')
                                pages.push(totalPages)
                              }
                            }
                            
                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                                    ...
                                  </span>
                                )
                              }
                              
                              const pageNum = page as number
                              const isActive = pageNum === currentPage
                              
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`${handles.paginationButton} min-w-[40px] px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isActive
                                      ? 'bg-blue-600 text-white border border-blue-600 shadow-sm'
                                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                                  }`}
                                  aria-label={`Ir para página ${pageNum}`}
                                  aria-current={isActive ? 'page' : undefined}
                                >
                                  {pageNum}
                                </button>
                              )
                            })
                          })()}
                        </div>
                        
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className={`${handles.paginationButton} inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors`}
                          aria-label="Próxima página"
                        >
                          Próxima
                          <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default BatteryCatalog
